import { Box } from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/**
 * Renders a custom tab panel that displays content conditionally based on the selected tab.
 * @example
 * CustomTabPanel({ children: <SomeComponent />, value: 0, index: 1 })
 * // Returns a hidden tab panel as value !== index
 * @param {TabPanelProps} props - The properties that define the tab panel's behavior and content.
 * @returns {JSX.Element} A tab panel component showing its children when active.
 * @description
 *   - The role of the div is set to "tabpanel" for accessibility purposes.
 *   - The tab panel visibility is controlled by comparing the `value` and `index`.
 *   - Includes spread operator to pass additional properties to the component.
 */
export default function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}
